import {
  type ContourOptions,
  WAVE_PRESET_NAMES,
  WAVE_PRESETS,
  type WaveParams,
} from "@sargasso/core";
import { render } from "preact";
import { useEffect, useRef, useState } from "preact/hooks";
import {
  type ResolvedCanvasOptions,
  SARGASSO_CHANGE_EVENT,
  type SargassoChangeDetail,
} from "./bridge";
import type { SargassoCanvasElement } from "./sargasso-canvas";
import CONTROLS_STYLE from "./sargasso-controls.css?inline";

interface SliderProps {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  onInput: (value: number) => void;
}

function Slider({ label, value, min, max, step, onInput }: SliderProps) {
  return (
    <label class="row">
      <span>{label}</span>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onInput={(e) => onInput(Number((e.currentTarget as HTMLInputElement).value))}
      />
      <span class="val">{value.toFixed(2)}</span>
    </label>
  );
}

type Rgba = [number, number, number, number];

function rgbToHex(c: Rgba): string {
  const part = (x: number) =>
    Math.round(Math.min(1, Math.max(0, x)) * 255)
      .toString(16)
      .padStart(2, "0");
  return `#${part(c[0])}${part(c[1])}${part(c[2])}`;
}

function hexToRgb(hex: string): [number, number, number] {
  const match = /^#?([0-9a-f]{6})$/i.exec(hex);
  const digits = match?.[1];
  if (!digits) return [1, 1, 1];
  const n = Number.parseInt(digits, 16);
  return [((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255];
}

function ColorRow({
  label,
  value,
  onInput,
}: {
  label: string;
  value: string;
  onInput: (hex: string) => void;
}) {
  return (
    <label class="row">
      <span>{label}</span>
      <input
        type="color"
        value={value}
        onInput={(e) => onInput((e.currentTarget as HTMLInputElement).value)}
      />
      <span class="val" />
    </label>
  );
}

/** The inner parameter form — shared by inline and overlay modes. */
function ControlsContent({ canvas }: { canvas: SargassoCanvasElement }) {
  const [opts, setOpts] = useState<ResolvedCanvasOptions>(() => canvas.options);

  useEffect(() => {
    // Refresh in case the canvas connected/changed between render and effect.
    setOpts(canvas.options);
    const onChange = (event: Event) => {
      setOpts((event as CustomEvent<SargassoChangeDetail>).detail.options);
    };
    canvas.addEventListener(SARGASSO_CHANGE_EVENT, onChange);
    return () => canvas.removeEventListener(SARGASSO_CHANGE_EVENT, onChange);
  }, [canvas]);

  const patchWave = (index: number, key: keyof WaveParams, value: number) => {
    const waves = opts.waves.map((w: WaveParams, i) => (i === index ? { ...w, [key]: value } : w));
    canvas.setOptions({ waves });
  };

  const addWave = () => {
    const next: WaveParams = { direction: 0, wavelength: 4, amplitude: 0.5, speed: 1 };
    canvas.setOptions({ waves: [...opts.waves, next] });
  };

  const removeWave = (index: number) => {
    canvas.setOptions({ waves: opts.waves.filter((_, i) => i !== index) });
  };

  const applyPreset = (name: string) => {
    const preset = WAVE_PRESETS[name as keyof typeof WAVE_PRESETS];
    if (preset) {
      canvas.setOptions({
        waves: preset.waves.map((w: WaveParams) => ({ ...w })),
        steepness: preset.steepness ?? 0,
      });
    }
  };

  const contour: ContourOptions = opts.contour ?? {
    interval: 0.5,
    width: 1.2,
    color: [0.82, 0.88, 0.98, 0.55],
  };
  const patchContour = (patch: Partial<ContourOptions>) => {
    canvas.setOptions({ contour: { ...contour, ...patch } });
  };
  const line: Rgba = contour.color;
  const index: Rgba = contour.emphasisColor ?? contour.color;

  return (
    <>
      <label class="preset">
        <span>preset</span>
        <select
          onChange={(e) => {
            const select = e.currentTarget as HTMLSelectElement;
            applyPreset(select.value);
            select.value = "";
          }}
        >
          <option value="">Load a preset…</option>
          {WAVE_PRESET_NAMES.map((name: string) => (
            <option key={name} value={name}>
              {name}
            </option>
          ))}
        </select>
      </label>

      <Slider
        label="global speed"
        value={opts.speed}
        min={0}
        max={3}
        step={0.05}
        onInput={(v) => canvas.setOptions({ speed: v })}
      />
      <Slider
        label="extent"
        value={opts.extent}
        min={4}
        max={60}
        step={1}
        onInput={(v) => canvas.setOptions({ extent: v })}
      />
      <Slider
        label="steepness"
        value={opts.steepness}
        min={0}
        max={1}
        step={0.05}
        onInput={(v) => canvas.setOptions({ steepness: v })}
      />
      <Slider
        label="interval"
        value={contour.interval}
        min={0.15}
        max={2}
        step={0.05}
        onInput={(v) => patchContour({ interval: v })}
      />
      <ColorRow
        label="line color"
        value={rgbToHex(line)}
        onInput={(hex) => patchContour({ color: [...hexToRgb(hex), line[3]] })}
      />
      <Slider
        label="line opacity"
        value={line[3]}
        min={0}
        max={1}
        step={0.05}
        onInput={(v) => patchContour({ color: [line[0], line[1], line[2], v] })}
      />
      <Slider
        label="line width"
        value={contour.width}
        min={0.5}
        max={6}
        step={0.1}
        onInput={(v) => patchContour({ width: v })}
      />
      <Slider
        label="index every"
        value={contour.emphasisEvery ?? 0}
        min={0}
        max={10}
        step={1}
        onInput={(v) => patchContour({ emphasisEvery: Math.round(v) })}
      />
      <ColorRow
        label="index color"
        value={rgbToHex(index)}
        onInput={(hex) => patchContour({ emphasisColor: [...hexToRgb(hex), index[3]] })}
      />
      <Slider
        label="index opacity"
        value={index[3]}
        min={0}
        max={1}
        step={0.05}
        onInput={(v) => patchContour({ emphasisColor: [index[0], index[1], index[2], v] })}
      />
      <Slider
        label="index width"
        value={contour.emphasisWidth ?? contour.width * 2}
        min={1}
        max={6}
        step={0.5}
        onInput={(v) => patchContour({ emphasisWidth: v })}
      />

      <div class="waves-head">
        <strong>waves ({opts.waves.length})</strong>
        <button type="button" onClick={addWave}>
          + add wave
        </button>
      </div>

      {opts.waves.length === 0 ? <p class="empty">No waves — add one, or load a preset.</p> : null}

      <div class="waves">
        {opts.waves.map((wave, i) => (
          <div class="wave" key={i}>
            <div class="wave-head">
              <span>wave {i + 1}</span>
              <button type="button" onClick={() => removeWave(i)}>
                remove
              </button>
            </div>
            <Slider
              label="direction"
              value={wave.direction}
              min={0}
              max={Math.PI * 2}
              step={0.05}
              onInput={(v) => patchWave(i, "direction", v)}
            />
            <Slider
              label="wavelength"
              value={wave.wavelength}
              min={0.3}
              max={20}
              step={0.1}
              onInput={(v) => patchWave(i, "wavelength", v)}
            />
            <Slider
              label="amplitude"
              value={wave.amplitude}
              min={0}
              max={2}
              step={0.05}
              onInput={(v) => patchWave(i, "amplitude", v)}
            />
            <Slider
              label="speed"
              value={wave.speed}
              min={0}
              max={5}
              step={0.05}
              onInput={(v) => patchWave(i, "speed", v)}
            />
          </div>
        ))}
      </div>
    </>
  );
}

/** Overlay wrapper: handle toggle + collapsible panel + optional fullscreen. */
function OverlayShell({
  canvas,
  host,
  open,
  showFullscreen,
}: {
  canvas: SargassoCanvasElement;
  host: SargassoControlsElement;
  open: boolean;
  showFullscreen: boolean;
}) {
  const panelRef = useRef<HTMLDivElement>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    const onChange = () => setIsFullscreen(document.fullscreenElement !== null);
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, []);

  const toggleFullscreen = () => {
    // Fullscreen the canvas's parent (the container holding both canvas and
    // controls), so the wave map and controls go immersive together.
    const target = canvas.parentElement;
    if (!target) return;
    if (document.fullscreenElement === target) {
      document.exitFullscreen();
    } else {
      target.requestFullscreen?.();
    }
  };

  return (
    <div class="overlay">
      <button
        class="handle"
        part="handle"
        type="button"
        onClick={() => host.toggleOpen()}
        aria-label={open ? "Close controls" : "Open controls"}
        aria-expanded={open}
      >
        {open ? "✕" : "⚙"}
      </button>
      <div class="panel" part="panel" ref={panelRef}>
        {showFullscreen && (
          <div class="panel-header">
            <button
              class="fullscreen-btn"
              part="fullscreen-button"
              type="button"
              onClick={toggleFullscreen}
              aria-label={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
            >
              {isFullscreen ? "⤢" : "⛶"}
            </button>
          </div>
        )}
        <ControlsContent canvas={canvas} />
      </div>
    </div>
  );
}

export const SARGASSO_CONTROLS_TOGGLE_EVENT = "sargasso-controls-toggle";

export interface SargassoControlsToggleDetail {
  open: boolean;
}

/**
 * `<sargasso-controls target="canvas-id">` — a Preact form that reads and writes
 * the parameters of a target `<sargasso-canvas>`. Point it at a canvas via the
 * `target` attribute (an element id) or the `for` property (the element itself).
 *
 * **Modes:**
 * - `mode="inline"` (default): the panel is always visible. No background is
 *   imposed — style the host element from outside.
 * - `mode="overlay"`: a small handle sits on one edge; click it to expand
 *   a panel that partially covers the canvas.
 *
 * **Overlay positioning:**
 * - `edge` — which edge the handle sits on and the panel slides from:
 *   `"right"` (default), `"left"`, `"top"`, or `"bottom"`. The active edge is
 *   reflected on `data-effective-edge` for CSS selectors.
 *
 * **Responsive layouts:** The library intentionally does no responsive
 * behavior. To vary the edge (or anything else) per breakpoint, render two
 * `<sargasso-controls>` elements targeting the same canvas and hide one with
 * a user-defined `@media` query — e.g.
 * `<sargasso-controls target="sea" edge="right"></sargasso-controls>`
 * `<sargasso-controls target="sea" edge="bottom" class="mobile-only"></sargasso-controls>`
 * with `.mobile-only { display: none; } @media (max-width: 600px) { … show it … }`.
 *
 * **Other overlay attributes:**
 * - `open` — reflects expanded state; toggle by clicking the handle.
 * - `fullscreen` — adds a fullscreen button to the panel.
 *
 * **Styling:** All visual properties (background, blur, width/height, radius,
 * …) are CSS custom properties on `:host` (`--sargasso-ctrl-bg`, `--sargasso-ctrl-blur`,
 * `--sargasso-ctrl-width`, `--sargasso-ctrl-height`, `--sargasso-ctrl-handle`,
 * `--sargasso-ctrl-radius`) and `::part(handle|panel|fullscreen-button)` selectors.
 */
export class SargassoControlsElement extends HTMLElement {
  static get observedAttributes(): readonly string[] {
    return ["target", "mode", "open", "fullscreen", "edge"];
  }

  private readonly root: ShadowRoot;
  private target: SargassoCanvasElement | null = null;

  constructor() {
    super();
    this.root = this.attachShadow({ mode: "open" });
  }

  connectedCallback(): void {
    this.reflectEdge();
    this.resolveTarget();
    this.renderApp();
  }

  disconnectedCallback(): void {
    render(null, this.root);
  }

  attributeChangedCallback(name: string): void {
    if (!this.isConnected) return;
    if (name === "edge") this.reflectEdge();
    this.resolveTarget();
    this.renderApp();
  }

  /** Reflect the `edge` attribute onto `data-effective-edge` for CSS selectors. */
  private reflectEdge(): void {
    const edge = this.getAttribute("edge") ?? "right";
    this.setAttribute("data-effective-edge", edge);
  }

  /** Toggle the `open` attribute (overlay mode). */
  toggleOpen(): void {
    if (this.hasAttribute("open")) {
      this.removeAttribute("open");
    } else {
      this.setAttribute("open", "");
    }
  }

  /** Set the target canvas element directly (alternative to the `target` id attribute). */
  set for(element: SargassoCanvasElement | null) {
    this.target = element;
    this.renderApp();
  }
  get for(): SargassoCanvasElement | null {
    return this.target;
  }

  private resolveTarget(): void {
    const id = this.getAttribute("target");
    if (!id) return;
    const scope = this.getRootNode() as Document | ShadowRoot;
    const found = scope.getElementById?.(id) ?? document.getElementById(id);
    this.target = (found as SargassoCanvasElement | null) ?? null;
  }

  private renderApp(): void {
    const mode = this.getAttribute("mode") ?? "inline";
    const open = this.hasAttribute("open");
    const showFullscreen = this.hasAttribute("fullscreen");

    if (!this.target) {
      render(
        <>
          <style>{CONTROLS_STYLE}</style>
          <p class="empty">No target canvas found.</p>
        </>,
        this.root,
      );
      if (mode === "overlay") {
        this.dispatchEvent(
          new CustomEvent(SARGASSO_CONTROLS_TOGGLE_EVENT, {
            detail: { open } satisfies SargassoControlsToggleDetail,
            bubbles: true,
            composed: true,
          }),
        );
      }
      return;
    }

    if (mode === "overlay") {
      render(
        <>
          <style>{CONTROLS_STYLE}</style>
          <OverlayShell
            canvas={this.target}
            host={this}
            open={open}
            showFullscreen={showFullscreen}
          />
        </>,
        this.root,
      );
    } else {
      render(
        <>
          <style>{CONTROLS_STYLE}</style>
          <div class="panel" part="panel">
            <ControlsContent canvas={this.target} />
          </div>
        </>,
        this.root,
      );
    }

    if (mode === "overlay") {
      this.dispatchEvent(
        new CustomEvent(SARGASSO_CONTROLS_TOGGLE_EVENT, {
          detail: { open } satisfies SargassoControlsToggleDetail,
          bubbles: true,
          composed: true,
        }),
      );
    }
  }
}
